<template>
  <footer>
    <div class="footer-main">
      <div class="footer-grid">
        <!-- Company Info Section -->
        <div class="footer-section company-section">
          <h3 class="section-title">{{ t('FOOTER.sections.brand') }}</h3>
          <p class="company-description">
            {{ t('FOOTER.company') }}
          </p>
          <div class="contact-info">
            <a href="https://discord.gg/veXFs2xstD" target="_blank" rel="noopener noreferrer" class="contact-link">
              <i class="fa-brands fa-discord"></i>
              {{ t('FOOTER.contact') }}
            </a>
          </div>
        </div>

        <!-- Legal Section -->
        <div class="footer-section legal-section">
          <h3 class="section-title">{{ t('FOOTER.sections.legal') }}</h3>
          <nav class="footer-links">
            <router-link :to="termsPath" :title="t('FOOTER.links.terms')">
              {{ t('FOOTER.links.terms') }}
            </router-link>
            <router-link :to="privacyPath" :title="t('FOOTER.links.privacy')">
              {{ t('FOOTER.links.privacy') }}
            </router-link>
            <router-link :to="withdrawalPath" :title="t('FOOTER.links.withdrawal')">
              {{ t('FOOTER.links.withdrawal') }}
            </router-link>
            <router-link :to="amlKycPath" :title="t('FOOTER.links.amlkyc')">
              {{ t('FOOTER.links.amlkyc') }}
            </router-link>
          </nav>
        </div>

        <!-- Social Section -->
        <div class="footer-section social-section">
          <h3 class="section-title">{{ t('FOOTER.sections.followUs') }}</h3>
          <div class="social-links">
            <a href="https://x.com/skinwhaless" target="_blank" rel="noopener noreferrer"
              :title="t('FOOTER.social.twitter')" :aria-label="t('FOOTER.social.twitter')" class="social-link">
              <i class="fa-brands fa-x-twitter"></i>
              <span>{{ t('FOOTER.social.labels.twitter') }}</span>
            </a>
            <a href="https://instagram.com/skinwhales" target="_blank" rel="noopener noreferrer"
              :title="t('FOOTER.social.instagram')" :aria-label="t('FOOTER.social.instagram')" class="social-link">
              <i class="fa-brands fa-instagram"></i>
              <span>{{ t('FOOTER.social.labels.instagram') }}</span>
            </a>
            <a href="https://t.me/skinwhales" target="_blank" rel="noopener noreferrer"
              :title="t('FOOTER.social.telegram')" :aria-label="t('FOOTER.social.telegram')" class="social-link">
              <i class="fa-brands fa-telegram"></i>
              <span>{{ t('FOOTER.social.labels.telegram') }}</span>
            </a>
            <a href="https://discord.gg/veXFs2xstD" target="_blank" rel="noopener noreferrer"
              :title="t('FOOTER.social.discord')" :aria-label="t('FOOTER.social.discord')" class="social-link">
              <i class="fa-brands fa-discord"></i>
              <span>{{ t('FOOTER.social.labels.discord') }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Bottom -->
    <div class="footer-bottom">
      <div class="footer-bottom-content">
        <p class="copyright">
          {{ t('FOOTER.copyright', { from: 2017, to: new Date().getFullYear() }) }}
        </p>
        <p class="disclaimer">
          {{ t('FOOTER.disclaimer') }}
        </p>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { addLocalePrefix, type Locale } from '@/utils/localeRouting'

const { t, locale } = useI18n()
defineOptions({ name: 'Footer' })

// Computed locale-aware paths
const termsPath = computed(() => addLocalePrefix(locale.value as Locale, '/legal/terms-of-service'))
const privacyPath = computed(() => addLocalePrefix(locale.value as Locale, '/legal/privacy-policy'))
const withdrawalPath = computed(() => addLocalePrefix(locale.value as Locale, '/legal/withdrawal-policy'))
const amlKycPath = computed(() => addLocalePrefix(locale.value as Locale, '/legal/aml-kyc'))
</script>

<style scoped lang="scss">
footer {
  background: linear-gradient(0deg, rgba(20, 25, 35, 0.5) 0%, $elevation1 100%);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-size: 0.9em;

  .footer-main {
    max-width: 1400px;
    margin: 0 auto;
    padding: 4em 2em 3em;

    @media (max-width: 768px) {
      padding: 3em 1.5em 2em;
    }
  }

  .footer-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 3em;

    @media (min-width: 768px) {
      grid-template-columns: 2fr 1fr 1fr;
      gap: 4em;
    }

    @media (max-width: 768px) {
      text-align: center;
    }
  }

  .footer-section {
    display: flex;
    flex-direction: column;
    gap: 1.25em;

    .section-title {
      font-size: 1.1em;
      font-weight: 700;
      letter-spacing: 0.5px;
      color: #ffffff;
      margin: 0 0 0.5em 0;
      text-transform: uppercase;
      position: relative;
      padding-bottom: 0.75em;

      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 40px;
        height: 2px;
        background: $accent-lime;

        @media (max-width: 768px) {
          left: 50%;
          transform: translateX(-50%);
        }
      }
    }
  }

  .company-section {
    .company-description {
      font-size: 0.95em;
      line-height: 1.7;
      color: rgba(255, 255, 255, 0.75);
      margin: 0 0 1em 0;
    }

    .contact-info {
      .contact-link {
        display: inline-flex;
        align-items: center;
        gap: 0.5em;
        color: rgba(255, 255, 255, 0.8);
        text-decoration: none;
        font-size: 0.95em;
        transition: color 0.2s ease;
        padding: 0.5em 0;

        i {
          font-size: 1.1em;
          color: $accent-lime;
        }

        &:hover {
          color: #ffffff;
        }

        @media (max-width: 768px) {
          justify-content: center;
        }
      }
    }
  }

  .legal-section {
    .footer-links {
      display: flex;
      flex-direction: column;
      gap: 0.75em;

      a {
        color: rgba(255, 255, 255, 0.8);
        text-decoration: none;
        font-size: 0.95em;
        transition: color 0.2s ease;
        padding: 0.25em 0;
        display: inline-block;

        &:hover {
          color: #ffffff;
        }
      }
    }
  }

  .social-section {
    .social-links {
      display: flex;
      flex-direction: column;
      gap: 0.75em;

      @media (max-width: 768px) {
        flex-direction: row;
        justify-content: center;
        gap: 1.5em;
        margin-top: 0.5em;
      }

      .social-link {
        display: flex;
        align-items: center;
        gap: 0.75em;
        color: rgba(255, 255, 255, 0.8);
        text-decoration: none;
        font-size: 0.95em;
        transition: color 0.2s ease;
        padding: 0.5em 0;

        @media (max-width: 768px) {
          padding: 0;
        }

        i {
          font-size: 1.4em;
          width: 24px;
          color: $accent-lime;

          @media (max-width: 768px) {
            font-size: 2em;
            width: auto;
          }
        }

        span {
          flex: 1;

          @media (max-width: 768px) {
            display: none;
          }
        }

        &:hover {
          color: #ffffff;
        }
      }
    }
  }

  .footer-bottom {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.2);
    margin-top: 2em;

    .footer-bottom-content {
      max-width: 1400px;
      margin: 0 auto;
      padding: 2em 2em;
      display: flex;
      flex-direction: column;
      gap: 1em;
      text-align: center;

      @media (max-width: 768px) {
        padding: 1.5em 1.5em;
      }

      p {
        margin: 0;
        font-size: 0.85em;
        color: rgba(255, 255, 255, 0.6);
        line-height: 1.6;
      }

      .copyright {
        font-weight: 500;
        color: rgba(255, 255, 255, 0.75);
      }

      .disclaimer {
        font-size: 0.8em;
      }
    }
  }
}
</style>
